import { useMemo } from 'react';
import { useLocation, useParams, type Location } from 'react-router-dom';

import { getAllArtifacts, type ArtifactId } from '@/artifacts';
import { mapRoutes } from '@/routes/routes';

/**
 * Shape of `location.state` when a modal/overlay navigation stores
 * its background (parent) location for the modal pattern.
 */
export interface BackgroundLocationState {
  backgroundLocation?: Location;
}

/**
 * Identifies which background artifact (tasks/notes/...) owns the
 * current `/map` path. Kept as a re-export of the registry-wide
 * `ArtifactId` so callers that previously referenced the literal
 * union (`'tasks' | 'notes'`) keep compiling.
 */
export type BackgroundArtifact = ArtifactId;

/**
 * Overlay path prefixes recognised inside `/map`. Derived from the
 * artifact registry so adding a new artifact automatically extends the
 * overlay detection set.
 *
 * **Prefixes must end with `/`** so that `/tasks` (background) is NOT
 * mistaken for `/task` (overlay) via `startsWith`.
 *
 * Computed lazily on first call to avoid an ES-module circular import:
 * importing the registry at module top-level pulls in artifact panel
 * components, which (transitively) re-import this hook. The cycle is
 * fine at runtime as long as we don't read from the registry until
 * after both modules have finished initialising.
 */
let _overlayPrefixes: readonly string[] | null = null;
function getOverlayPrefixes(): readonly string[] {
  if (_overlayPrefixes === null) {
    _overlayPrefixes = getAllArtifacts().map(
      (artifact) => `/${artifact.singular}/`
    );
  }
  return _overlayPrefixes;
}

/**
 * Returns `true` if the given path represents an overlay route
 * (e.g. `/map/task/...` or `/map/note/...`).
 */
export function isOverlayPath(pathname: string): boolean {
  // Match /w/<shortId>/map/<singular>/...
  const mapIndex = pathname.indexOf('/map/');
  if (mapIndex === -1) return false;
  const afterMap = pathname.slice(mapIndex + '/map'.length);
  return getOverlayPrefixes().some((prefix) => afterMap.startsWith(prefix));
}

/**
 * Returns the active background artifact id (`tasks`/`notes`/...) for
 * the given background path. Defaults to the first registered artifact
 * when the path is `/map` or otherwise unrecognised.
 */
export function resolveBackgroundArtifact(pathname: string): ArtifactId {
  for (const artifact of getAllArtifacts()) {
    if (pathname.includes(`/map/${artifact.id}`)) {
      return artifact.id as ArtifactId;
    }
  }
  // Default to the first registered artifact (currently `tasks`).
  return (getAllArtifacts()[0]?.id ?? 'tasks') as ArtifactId;
}

/**
 * Hook that returns the "background" location for the React Router modal
 * pattern.
 *
 * - If current path is an overlay (e.g. `/map/task/:id`), we return
 *   `location.state.backgroundLocation` when available; otherwise a safe
 *   default (`/map/tasks`) so the refresh case still renders a background.
 * - If current path is a regular background path (e.g. `/map/tasks`), we
 *   return the current location unchanged — there is no "behind" location
 *   because the user is not in an overlay.
 */
export function useBackgroundLocation(): Location {
  const location = useLocation();
  const { shortId } = useParams<{ shortId: string }>();
  const state = location.state as BackgroundLocationState | null;

  const fallbackArtifactId = resolveBackgroundArtifact(location.pathname);
  const defaultPath = shortId
    ? mapRoutes.background(fallbackArtifactId, shortId)
    : '/';

  // Memoize the fallback Location so the reference is stable across
  // re-renders. Without this, a new object on every render defeats
  // memo() on MapBackgroundRoutes and causes excessive re-renders.
  const fallbackLocation = useMemo(
    () =>
      ({
        pathname: defaultPath,
        search: '',
        hash: '',
        state: null,
        key: 'default',
      }) as Location,
    [defaultPath]
  );

  if (!isOverlayPath(location.pathname)) {
    return location;
  }

  if (state?.backgroundLocation) {
    return state.backgroundLocation;
  }

  return fallbackLocation;
}

/**
 * Returns the raw overlay location (the current location) or `null` if
 * the user is not in an overlay.
 */
export function useOverlayLocation(): Location | null {
  const location = useLocation();
  return isOverlayPath(location.pathname) ? location : null;
}
